import React from "react";
import { Select, Space, Checkbox, Spin, Form, Input, Tooltip } from "antd";
import { handleMoneyChange } from "../../utils/moneyInputUtils";
import { usePeriodAvailability } from "../../hooks/usePeriodAvailability";

const monthsData = [
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

/**
 * Composant tableau de saisie des déclarations
 * Réutilisable pour création et édition de déclarations
 *
 * @param {Object} props - Props du composant
 * @param {string} props.mode - Mode du formulaire ('create', 'edit', 'view')
 * @param {string} props.formType - Type de formulaire ('html' ou 'antd')
 * @param {Object} props.salary - État des salaires
 * @param {Object} props.taxes - État des taxes calculées
 * @param {boolean} props.isInputLoading - État de chargement des calculs
 * @param {boolean} props.conditionCheckbox - Afficher la checkbox optionnelle ouvriers
 * @param {Function} props.onSalaryChange - Handler changement de salaire (HTML form)
 * @param {Function} props.onMoneyFieldChange - Handler changement champ monétaire (HTML form)
 * @param {Function} props.onMonthChange - Handler changement de mois
 * @param {Function} props.onYearChange - Handler changement d'année
 * @param {Object} props.form - Instance Ant Design Form (pour mode 'antd')
 * @param {Function} props.getCurrentYearToString - Fonction pour obtenir l'année courante
 */
const DeclarationFormTable = ({
  mode = "create",
  formType = "html",
  salary,
  taxes,
  isInputLoading = false,
  conditionCheckbox = false,
  onSalaryChange,
  onMoneyFieldChange,
  onMonthChange,
  onYearChange,
  onSemesterChange,
  form = null,
  getCurrentYearToString = () => new Date().getFullYear().toString(),
  isAnnual = false,
  isSemiAnnual = false,
  semester = null,
  availableYears = [],
  isYearUnavailable = undefined,
}) => {
  const isDisabled = mode === "view";
  const isAntdForm = formType === "antd";

  const {
    isMonthAvailable,
    isSemesterAvailable,
    isYearAvailable,
    getUnavailableMessage,
    periodsForSelects,
  } = usePeriodAvailability();

  /**
   * Rendu d'un champ de saisie monétaire
   */
  const renderMoneyInput = (name, value, required = true) => {
    if (isAntdForm) {
      return (
        <Form.Item
          name={name}
          rules={[
            {
              required,
              message: `Veuillez entrer ${name}`,
            },
          ]}
        >
          <Input
            type="text"
            inputMode="numeric"
            disabled={isDisabled}
            onChange={(event) => {
              const value = event.target.value;
              const formattedValue = handleMoneyChange(value);
              form?.setFieldsValue({ [name]: formattedValue });
            }}
          />
        </Form.Item>
      );
    }

    // HTML form
    return (
      <input
        type="text"
        name={name}
        onChange={onMoneyFieldChange}
        value={value ?? ""}
        inputMode="numeric"
        required={required}
        disabled={isDisabled}
      />
    );
  };

  /**
   * Rendu d'un champ lecture seule avec loading
   */
  const renderReadOnlyField = (name, value) => {
    if (isAntdForm) {
      return (
        <div className="input-container">
          {isInputLoading && (
            <div className="spin-overlay">
              <Spin />
            </div>
          )}
          <Form.Item name={name}>
            <Input readOnly tabIndex="-1" />
          </Form.Item>
        </div>
      );
    }

    // HTML form
    if (isInputLoading) {
      return (
        <div className="input-style">
          <Spin spinning={isInputLoading} />
        </div>
      );
    }

    return (
      <input
        type="text"
        name={name}
        className="text-align-right"
        value={value ?? ""}
        required
        readOnly
        tabIndex="-1"
      />
    );
  };

  /**
   * Rendu du sélecteur de mois
   */
  const renderMonthSelect = () => {
    const getSelectedYear = () => {
      if (isAntdForm) {
        return form?.getFieldValue("year") || salary?.year;
      }
      return salary?.year;
    };

    const selectedYear = getSelectedYear();

    const monthsForYear = periodsForSelects?.monthly?.months?.[selectedYear] || [];

    const monthOptions = monthsForYear.map((monthData) => {
      const monthInfo = monthsData.find(m => parseInt(m.value) === monthData.month);

      return {
        value: monthData.month.toString(),
        label: monthData.available ? monthInfo.label : `${monthInfo.label} (déjà déclaré)`,
        disabled: !monthData.available,
      };
    });

    if (isAntdForm) {
      return (
        <Form.Item name="month">
          <Space wrap>
            <Select
              className="select"
              disabled={isDisabled}
              value={monthsData[salary?.month - 1]}
              onChange={onMonthChange}
              options={monthOptions}
            />
          </Space>
        </Form.Item>
      );
    }

    // HTML form
    return (
      <Space wrap>
        <Select
          value={salary?.month}
          className="select"
          onChange={onMonthChange}
          disabled={isDisabled}
          options={monthOptions}
        />
      </Space>
    );
  };

  /**
   * Rendu du sélecteur d'année
   */
  const renderYearSelect = () => {
    let yearOptions = [];

    if (isAnnual) {
      const annualYears = periodsForSelects?.annual?.years || [];
      yearOptions = annualYears.map(({ year, available }) => ({
        value: year.toString(),
        label: available ? year.toString() : `${year} (déjà déclaré)`,
        disabled: !available,
      }));
    } else if (isSemiAnnual) {
      const semiAnnualYears = periodsForSelects?.semiAnnual?.years || [];
      yearOptions = semiAnnualYears.map(({ year, available }) => ({
        value: year.toString(),
        label: available ? year.toString() : `${year} (déjà déclaré)`,
        disabled: !available,
      }));
    } else {
      const monthlyYears = periodsForSelects?.monthly?.years || [];
      yearOptions = monthlyYears.map(({ year, available }) => ({
        value: year.toString(),
        label: available ? year.toString() : `${year} (déjà déclaré)`,
        disabled: !available,
      }));
    }

    // Pour les déclarations annuelles, le defaultValue est la première année disponible
    const getDefaultYear = () => {
      if (isAnnual && availableYears.length > 0) {
        return availableYears[0].toString();
      }
      return getCurrentYearToString();
    };

    if (isAntdForm) {
      return (
        <Form.Item name="year">
          <Space wrap>
            <Select
              className="select"
              disabled={isDisabled || (isAnnual && availableYears.length === 0)}
              defaultValue={salary?.year || getDefaultYear()}
              onChange={onYearChange}
              options={yearOptions}
              placeholder={isAnnual && availableYears.length === 0 ? "Chargement..." : undefined}
            />
          </Space>
        </Form.Item>
      );
    }

    // HTML form
    return (
      <Space wrap>
        <Select
          data-testid="year"
          value={salary?.year}
          className="select"
          onChange={onYearChange}
          disabled={isDisabled || (isAnnual && availableYears.length === 0)}
          options={yearOptions}
          placeholder={isAnnual && availableYears.length === 0 ? "Chargement..." : undefined}
        />
      </Space>
    );
  };

  /**
   * Rendu du sélecteur de semestre
   */
  const renderSemesterSelect = () => {
    const selectedYear = salary?.year ? salary.year.toString() : null;
    const semestersForYear = periodsForSelects?.semiAnnual?.semesters?.[selectedYear] || [];

    const semesterLabels = {
      1: "S1 (Janvier - Juin)",
      2: "S2 (Juillet - Décembre)",
    };

    const semesterOptions = semestersForYear.map((semesterData) => {
      const baseLabel = semesterLabels[semesterData.semester];

      return {
        value: semesterData.semester,
        label: semesterData.available ? baseLabel : `${baseLabel} (déjà déclaré)`,
        disabled: !semesterData.available,
      };
    });

    if (isAntdForm) {
      return (
        <Form.Item name="semester">
          <Space wrap>
            <Select
              className="select"
              disabled={isDisabled}
              value={semester}
              onChange={onSemesterChange}
              options={semesterOptions}
            />
          </Space>
        </Form.Item>
      );
    }

    // HTML form
    return (
      <Space wrap>
        <Select
          data-testid="semester"
          value={semester}
          className="select"
          onChange={onSemesterChange}
          disabled={isDisabled}
          options={semesterOptions}
        />
      </Space>
    );
  };

  /**
   * Rendu du champ effectifs
   */
  const renderWorkForceInput = () => {
    if (isAntdForm) {
      return (
        <Form.Item
          name="workForce"
          rules={[
            {
              required: true,
              message: "Veuillez entrer le nombre des effectifs",
            },
          ]}
        >
          <Input min={0} step={1} type="number" disabled={isDisabled} />
        </Form.Item>
      );
    }

    // HTML form
    return (
      <input
        type="number"
        min={0}
        step={1}
        name="workForce"
        value={salary?.workForce ?? ""}
        onChange={onSalaryChange}
        inputMode="numeric"
        required
        disabled={isDisabled}
      />
    );
  };

  /**
   * Rendu de la checkbox optionnelle ouvriers
   */
  const renderOptionalWorkerCheckbox = () => {
    if (!conditionCheckbox) {
      return null;
    }

    if (isAntdForm) {
      return (
        <Form.Item name="agreeOptionalWorker" valuePropName="checked">
          <Checkbox
            checked={salary?.agreeOptionalWorker}
            disabled={isDisabled}
            required
          >
            J'ai pris connaissance que la souscription aux Prestations pour les
            Ouvriers est optionnelle si les effectifs sont supérieurs à 10.
          </Checkbox>
        </Form.Item>
      );
    }

    // HTML form
    return (
      <div className="flex-row visible">
        <div>
          <Checkbox
            onChange={onSalaryChange}
            name="agreeOptionalWorker"
            checked={salary?.agreeOptionalWorker}
            style={{ marginTop: "20px", paddingBottom: "30px" }}
            required
          >
            J'ai pris connaissance que la souscription aux Prestations pour les
            Ouvriers est optionnelle si les effectifs sont supérieurs à 10.
          </Checkbox>
        </div>
      </div>
    );
  };

  return (
    <>
      <table>
        <thead>
          <tr>
            <th scope="col"></th>
            <th scope="col">Salaire</th>
            <th scope="col">Cotisation (0,4%)</th>
            <th scope="col">TVA</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>Salaire Brut Cadres</th>
            <td>{renderMoneyInput("salaryExecutive", salary?.salaryExecutive)}</td>
            <td>
              {renderReadOnlyField(
                "salaryExecutiveFee",
                `${taxes?.feeExecutive} €`
              )}
            </td>
            <td>
              {renderReadOnlyField(
                "salaryExecutiveTva",
                `${taxes?.tvaExecutive} €`
              )}
            </td>
          </tr>

          <tr>
            <th>Salaire Brut ETAM</th>
            <td>{renderMoneyInput("salaryEtam", salary?.salaryEtam)}</td>
            <td>
              {renderReadOnlyField("salaryEtamFee", `${taxes?.feeEtam} €`)}
            </td>
            <td>
              {renderReadOnlyField("salaryEtamTva", `${taxes?.tvaEtam} €`)}
            </td>
          </tr>

          <tr>
            <th>
              Salaire Brut Ouvriers{" "}
              {salary?.workForce > 10 ? "(opt.)" : ""}
            </th>
            <td>
              {renderMoneyInput("salaryWorker", salary?.salaryWorker, false)}
            </td>
            <td>
              {renderReadOnlyField("salaryWorkerFee", `${taxes?.feeWorker} €`)}
            </td>
            <td>
              {renderReadOnlyField("salaryWorkerTva", `${taxes?.tvaWorker} €`)}
            </td>
          </tr>

          <tr>
            <th>Date</th>
            {isSemiAnnual ? (
              <td data-testid="testSemester">{renderSemesterSelect()}</td>
            ) : (
              !isAnnual && <td data-testid="testMonth">{renderMonthSelect()}</td>
            )}
            <td>{renderYearSelect()}</td>
            <td></td>
          </tr>

          <tr>
            <th>Effectifs</th>
            <td>{renderWorkForceInput()}</td>
            <td></td>
            <td></td>
          </tr>

          <tr>
            <th>Montant dû</th>
            <td>
              {renderReadOnlyField("feeAmount", `${taxes?.feeAmount} €`)}
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>

      {renderOptionalWorkerCheckbox()}
    </>
  );
};

export default DeclarationFormTable;
